/**
 * דף מוצר — TASKS 4.2 · PRD 8.1, 7.4, 7.5.
 *
 * שם · תיאור · תמונה — שלושתם חובה (8.1). Lightbox נגיש · טבלת מפרט · וידאו
 * · שדות מידות לפי סוג תמחור · "הוסף לעגלה". ⛔ **אין מחיר בשום מקום** (6.5).
 *
 * ⭐ Feedback1.md סעיף 8.3/8.6 (20.08.2026) — הקטלוג עבר למבנה שטוח (`CatalogPage`,
 *    בלי דף קטגוריה נפרד), אבל **ה-URL של דף המוצר עצמו נשאר `/catalog/:categorySlug/:sku`
 *    כפי שהוא** (0.5, מגן על קישורים שכבר אונדקסו). קליק על כרטיס ואריאנט
 *    בקטלוג מעביר `?variant=<sku>` כדי שהוואריאנט הנכון ייבחר כבר בעלייה —
 *    ⛔ לא תמיד הראשון. פירורי הלחם: קישור הקטגוריה (שדף הקטגוריה שלו כבר
 *    לא קיים) הפך לטקסט רגיל לא-לחיץ.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import {
  getProduct,
  isPublicApiError,
  type PublicMedia,
  type PublicProductDetail,
  type PublicVariant,
} from '../publicApi/catalogApi.js';
import { COMPANY_NAME } from '../content/businessInfo.js';
import { Lightbox, type LightboxImage } from '../components/Lightbox.js';
import { PricingFields, type PricingFieldsResult } from '../product/PricingFields.js';
import { useCart } from '../cart/CartContext.js';

/**
 * ⭐ קבוצת-גודל של ואריאנט (`attrs.sizeGroup`) — משמשת ליפול חזרה על תמונת
 * ואריאנט "אח" באותה מידה כשלוואריאנט הנבחר (למשל צבע חדש) עדיין אין תמונה
 * משלו. ⛔ לא לתמחור ולא לחיפוש (כמו כל `attrs`, ראו 006_product_variants.sql) —
 * רק כדי שבחירת צבע שני לא תציג "אין תמונה זמינה" כשקיימת כבר תמונה של אותה
 * מידה בצבע אחר.
 */
function sizeGroupOf(variant: PublicVariant | null | undefined): string | null {
  const raw = variant?.attrs?.['sizeGroup'];
  return typeof raw === 'string' ? raw : null;
}

/** תווית תצוגה למידה (למשל "6 אינץ׳"), נפרדת מ-`sizeGroup` הטכני ("06"). */
function sizeLabelOf(variant: PublicVariant | null | undefined): string | null {
  const raw = variant?.attrs?.['sizeLabel'];
  return typeof raw === 'string' ? raw : null;
}

function colorOf(variant: PublicVariant | null | undefined): string | null {
  const raw = variant?.attrs?.['color'];
  return typeof raw === 'string' ? raw : null;
}

/**
 * ⭐ Feedback1.md סעיף 13 (20.08.2026) — `MG-0001` ("גמיש למאייד נסתר") מקבל
 * ציר שני "מוכן/לא-מוכן" במקום "צבע" (`TR-0003`). ⚠️ **קידוד ידני ייעודי,
 * ⛔ לא מנגנון גנרי** — Feedback1.md סעיף 16.4 אוסר במפורש הרחבה לציר שלישי
 * עתידי ניתן-להגדרה בפאנל; רק שני הצירים הקיימים (מידה/צבע) הופכים למתג.
 * "מוכן/לא-מוכן" נשאר עבודת פיתוח נקודתית כמו כאן, לא הרחבה של אותו מתג.
 */
function readyStateOf(variant: PublicVariant | null | undefined): 'ready' | 'not_ready' | null {
  const raw = variant?.attrs?.['ready'];
  return raw === 'ready' || raw === 'not_ready' ? raw : null;
}

const READY_STATE_LABEL: Record<'ready' | 'not_ready', string> = {
  ready: 'מוכן',
  not_ready: 'לא מוכן',
};

/** ⭐ רשימה ממוינת של ערכים ייחודיים (מידות/צבעים), לפי סדר ההופעה הראשון. */
function distinctValues(
  variants: readonly PublicVariant[],
  get: (v: PublicVariant) => string | null,
) {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const v of variants) {
    const value = get(v);
    if (value !== null && !seen.has(value)) {
      seen.add(value);
      result.push(value);
    }
  }
  return result;
}

function mediaForScope(
  media: PublicMedia[],
  variantId: number | null,
  siblingVariantIds: readonly number[],
): PublicMedia[] {
  const own = media.filter((m) => m.variantId === variantId);
  if (own.length > 0) return own;
  for (const siblingId of siblingVariantIds) {
    const sibling = media.filter((m) => m.variantId === siblingId);
    if (sibling.length > 0) return sibling;
  }
  return media.filter((m) => m.variantId === null);
}

export function ProductPage() {
  const { sku = '' } = useParams<{ categorySlug: string; sku: string }>();
  const [searchParams] = useSearchParams();
  const variantSkuParam = searchParams.get('variant');

  const [data, setData] = useState<{
    product: PublicProductDetail;
    media: PublicMedia[];
    variants: PublicVariant[];
  } | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [pricingResult, setPricingResult] = useState<PricingFieldsResult | null>(null);
  const [addedMessage, setAddedMessage] = useState<string | null>(null);
  // ⭐ קובץ תמונה שקיים ב-DB אבל שבור בפועל בשרת (404) - ⛔ לא אמור להציג
  // ריבוע ריק/שבור, אלא ליפול לאותה תצוגת "אין תמונה זמינה" שכבר קיימת.
  const [brokenMediaIds, setBrokenMediaIds] = useState<ReadonlySet<number>>(new Set());

  const cart = useCart();

  useEffect(() => {
    setData(null);
    setNotFound(false);
    setError(null);
    setSelectedVariantId(null);
    setAddedMessage(null);
    setBrokenMediaIds(new Set());

    getProduct(sku)
      .then((result) => {
        setData(result);
        // ⭐ Feedback1.md סעיף 8.3 — ואריאנט שהגיע מקישור כרטיס בקטלוג
        // (`?variant=<sku>`) נבחר מראש; נופל לוואריאנט הראשון כברירת מחדל
        // (התנהגות קיימת) כשאין פרמטר, או כשה-SKU לא תואם אף ואריאנט.
        const fromQuery = variantSkuParam
          ? result.variants.find((v) => v.sku === variantSkuParam)
          : undefined;
        setSelectedVariantId(fromQuery?.id ?? result.variants[0]?.id ?? null);
      })
      .catch((err) => {
        if (isPublicApiError(err) && err.status === 404) setNotFound(true);
        else setError(isPublicApiError(err) ? err.message : 'שגיאה בטעינת המוצר.');
      });
  }, [sku, variantSkuParam]);

  // ⭐ נמצא 21.08.2026 — `PublicShell.tsx` מסנכרן כותרת טאב לעמודים סטטיים,
  // ⛔ אבל לא יודע את שם המוצר. אותו מבנה טקסט בדיוק כמו `seoMeta.ts`
  // (השרת, לטעינה ראשונה/וואטסאפ) — כפילות מכוונת, לא מקור אמת משותף.
  useEffect(() => {
    if (data) {
      document.title = `${data.product.name} | ${data.product.category.name} | ${COMPANY_NAME}`;
    }
  }, [data]);

  const siblingVariantIds = useMemo(() => {
    if (!data) return [];
    const current = data.variants.find((v) => v.id === selectedVariantId) ?? null;
    const group = sizeGroupOf(current);
    if (!group) return [];
    return data.variants
      .filter((v) => v.id !== selectedVariantId && sizeGroupOf(v) === group)
      .map((v) => v.id);
  }, [data, selectedVariantId]);

  const scopedMedia = useMemo(
    () => (data ? mediaForScope(data.media, selectedVariantId, siblingVariantIds) : []),
    [data, selectedVariantId, siblingVariantIds],
  );
  const images = useMemo(
    () => scopedMedia.filter((m) => m.type === 'image').filter((m) => !brokenMediaIds.has(m.id)),
    [scopedMedia, brokenMediaIds],
  );
  const video = useMemo(() => scopedMedia.find((m) => m.type === 'video'), [scopedMedia]);

  const lightboxImages: LightboxImage[] = useMemo(
    () => images.map((img) => ({ url: img.url, alt: img.alt || data?.product.name || '' })),
    [images, data],
  );

  /**
   * ⚠️ ⛔ **`layoutId` רק על התמונה הראשונה שאי-פעם הוצגה, ⛔ לא על כל תמונה.**
   * נתפס בפועל ב-E2E (`catalog.spec.ts`, "בחירת ואריאנט מחליפה את התמונה
   * הראשית"): כל עוד `layoutId` היה קבוע (`product-image-${sku}`) על **כל**
   * `motion.img`, החלפת ואריאנט יצרה לרגע שתי תמונות עם אותו `layoutId`
   * בו-זמנית (היוצאת + הנכנסת) — ומנגנון ה-shared-layout של `motion` השתלט
   * על ה-crossfade ומנע מהיוצאת להיעלם (נשארה תקועה לצמיתות, לא רק לרגע
   * המעבר). `hasEnteredRef` נקבע ל-`false` פעם אחת אחרי העלייה הראשונה —
   * מאותו רגע כל תמונה חדשה (מעבר ואריאנט) מקבלת `layoutId={undefined}`,
   * ורק התמונה **היוצאת** (שנרכבה בזמן שה-ref עוד היה `true`) ממשיכה לשאת
   * את ה-layoutId המקורי בזמן שהיא דוהה — בלי התנגשות.
   */
  const hasEnteredRef = useRef(true);
  useEffect(() => {
    hasEnteredRef.current = false;
  }, []);
  const mainImageLayoutId = hasEnteredRef.current ? `product-image-${sku}` : undefined;

  if (notFound) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <h1 className="text-2xl font-bold">המוצר לא נמצא</h1>
        <Link to="/catalog" className="btn btn-secondary mt-6 inline-flex">
          חזרה לקטלוג
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <p role="alert" className="text-danger">
          {error}
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-16">
        <p className="sr-only" aria-live="polite">
          טוען מוצר…
        </p>
        <div className="grid gap-10 lg:grid-cols-2" aria-hidden="true">
          <div className="skeleton-shimmer aspect-square rounded-lg border border-line" />
          <div className="space-y-3">
            <div className="skeleton-shimmer h-8 w-3/4 rounded" />
            <div className="skeleton-shimmer h-4 w-1/3 rounded" />
            <div className="skeleton-shimmer mt-6 h-11 w-40 rounded-md" />
          </div>
        </div>
      </div>
    );
  }

  const { product, variants } = data;
  const selectedVariant = variants.find((v) => v.id === selectedVariantId) ?? null;
  // ⭐ ואריאנטים עם ציר שני (`attrs.color` — TR-0003, או `attrs.ready` —
  // MG-0001) הם צירוף מידה×ציר-שני — במקום רשימה שטוחה אחת (כמו ב-SH-0001)
  // הבורר מתפצל לשתי שורות עצמאיות: מידה וציר שני, כל אחת עם ה-radiogroup
  // המלא שלה. הבחירה בפועל תמיד פותרת ל-SKU (ואריאנט) יחיד — משלבים את שתי
  // הבחירות בעריכת אירוע ⛔ לא ב-state נפרד. ⚠️ **שני הצירים בלבד, קבועים
  // בקוד** (Feedback1.md סעיף 16.4) — ⛔ לא מנגנון גנרי לציר שלישי עתידי.
  const axis2Type: 'color' | 'ready' | null = variants.some((v) => colorOf(v) !== null)
    ? 'color'
    : variants.some((v) => readyStateOf(v) !== null)
      ? 'ready'
      : null;
  const axis2Of = axis2Type === 'color' ? colorOf : readyStateOf;
  const axis2Label = axis2Type === 'color' ? 'בחרו צבע*' : 'מוכן / לא מוכן*';
  const sizeOptions = axis2Type
    ? distinctValues(variants, (v) => sizeGroupOf(v)).map((sizeGroup) => ({
        sizeGroup,
        label: sizeLabelOf(variants.find((v) => sizeGroupOf(v) === sizeGroup)) ?? sizeGroup,
      }))
    : [];
  const axis2Options = axis2Type
    ? distinctValues(variants, axis2Of).map((value) => ({
        value,
        label: axis2Type === 'ready' ? READY_STATE_LABEL[value as 'ready' | 'not_ready'] : value,
      }))
    : [];

  function selectSize(sizeGroup: string) {
    const currentAxis2 = axis2Of(selectedVariant);
    const match =
      variants.find((v) => sizeGroupOf(v) === sizeGroup && axis2Of(v) === currentAxis2) ??
      variants.find((v) => sizeGroupOf(v) === sizeGroup);
    if (match) setSelectedVariantId(match.id);
  }

  function selectAxis2(value: string) {
    const currentSize = sizeGroupOf(selectedVariant);
    const match =
      variants.find((v) => axis2Of(v) === value && sizeGroupOf(v) === currentSize) ??
      variants.find((v) => axis2Of(v) === value);
    if (match) setSelectedVariantId(match.id);
  }

  // ⭐ Feedback1.md סעיף 13.3 — MG-0001 מקבל שדה כמות יחיד במטרים.
  // ⭐ 24.08.2026 — MG-0002 ("מתאם גמיש למאייד נסתר") מצטרף: מוצר מוגמר של
  // MG-0001 עצמו, קיים באותן 3 המידות (ראה הוואריאנטים שנוספו לו), ונמכר
  // באותו אופן בדיוק — בחירת מידה + כמות יחידה, ⛔ לא "אורך נדרש". ⚠️ אבל
  // בלי המילה "מטרים" בתווית (`singleQuantityFieldLabel`) — מוצר מוגמר,
  // ⛔ לא חומר גלם שנמדד במטרים כמו MG-0001.
  const singleQuantityField = product.sku === 'MG-0001' || product.sku === 'MG-0002';
  const singleQuantityFieldLabel = product.sku === 'MG-0002' ? 'כמות*' : undefined;

  function handleAddToCart() {
    if (!pricingResult?.isValid) return;
    cart.addItem({
      productId: product.id,
      sku: product.sku,
      productName: product.name,
      variantId: selectedVariant?.id ?? null,
      variantLabel: selectedVariant?.label ?? null,
      productImage: images[0]
        ? { url: images[0].thumbUrl, alt: images[0].alt || product.name }
        : null,
      unitLabel: product.unitLabel,
      pricingType: product.pricingType,
      minOrderQty: product.minOrderQty,
      qtyStep: product.qtyStep,
      measurements: pricingResult.measurements,
    });
    setAddedMessage(`${product.name} נוסף לעגלת הבקשות.`);
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      {/* ⚠️ underline על הקישור — axe (E2E) תפס contrast 1.21:1 מול הטקסט
          המשני שמסביב, מתחת לסף 3:1 להבחנה שאינה מבוססת צבע (WCAG 1.4.1).
          ⭐ Feedback1.md סעיף 8.6 — קישור הקטגוריה הפך לטקסט לא-לחיץ: דף
          הקטגוריה שלו כבר לא קיים (הקטלוג עבר למבנה שטוח, סעיף 8). */}
      <nav aria-label="פירורי לחם" className="text-muted text-sm">
        <Link to="/catalog" className="text-link underline">
          קטלוג
        </Link>{' '}
        / {product.category.name}
      </nav>

      <div className="mt-4 grid gap-10 lg:grid-cols-2">
        {/* --- גלריה ------------------------------------------------------- */}
        <div>
          {images.length > 0 ? (
            <>
              <button
                type="button"
                className="product-gallery-main bg-canvas relative block w-full overflow-hidden rounded-lg border border-line"
                onClick={() => setLightboxIndex(0)}
                aria-label={`הגדלת תמונת ${product.name}`}
              >
                {/* ⭐ TASKS 6.2 · DESIGN.md 6.2, 6.5 — `layoutId` הוא ה-shared
                    element מהכרטיס בקטלוג (`ProductCard.tsx`, אותו מפתח לפי
                    SKU). ⚠️ **בכוונה בלי `mode="wait"`** — למרות שהטבלה ב-
                    DESIGN.md 6.2 מזכירה אותו לצד "crossfade": crossfade אמיתי
                    הוא חפיפה בין היוצא לנכנס, ו-`mode="wait"` בדיוק מבטל את
                    החפיפה הזו (מחכה שהיוצא ייעלם לגמרי, ואז מרכיב את הנכנס) —
                    התנהגות "fade-out ואז fade-in", ⛔ לא crossfade. ברירת
                    המחדל (ללא mode) גם משמרת בדיקה קיימת שבודקת החלפת תמונה
                    **סינכרונית** מיד אחרי לחיצה על ואריאנט (`ProductPage.test.tsx`). */}
                <AnimatePresence>
                  <motion.img
                    key={images[0]?.id}
                    layoutId={mainImageLayoutId}
                    src={images[0]?.cardUrl}
                    alt={images[0]?.alt || product.name}
                    onError={() => {
                      const id = images[0]?.id;
                      if (id === undefined) return;
                      setBrokenMediaIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="absolute inset-0 h-full w-full object-contain p-4"
                  />
                </AnimatePresence>
              </button>

              {images.length > 1 && (
                <ul className="mt-3 flex flex-wrap gap-2">
                  {images.map((img, i) => (
                    <li key={img.id}>
                      <button
                        type="button"
                        onClick={() => setLightboxIndex(i)}
                        className="bg-canvas block h-16 w-16 overflow-hidden rounded-md border border-line p-1"
                      >
                        <img
                          src={img.thumbUrl}
                          alt={img.alt || `תמונה ${i + 1}`}
                          loading="lazy"
                          width={64}
                          height={64}
                          onError={() =>
                            setBrokenMediaIds((prev) =>
                              prev.has(img.id) ? prev : new Set(prev).add(img.id),
                            )
                          }
                          className="h-full w-full object-contain"
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <div className="flex aspect-square w-full items-center justify-center rounded-lg border border-line bg-surface">
              <span className="text-muted">אין תמונה זמינה</span>
            </div>
          )}

          {video && (
            <video
              controls
              preload="none"
              poster={video.type === 'video' ? (video.posterUrl ?? undefined) : undefined}
              className="mt-4 w-full rounded-lg border border-line"
            >
              <source src={video.url} />
            </video>
          )}
        </div>

        {/* --- פרטים -------------------------------------------------------- */}
        <div>
          <h1 className="text-3xl font-bold">{product.name}</h1>
          <p className="ltr-inline text-muted mt-1 text-sm">
            {selectedVariant?.sku ?? product.sku}
          </p>

          {product.description && <p className="mt-4 whitespace-pre-line">{product.description}</p>}

          {axis2Type ? (
            <>
              <fieldset className="mt-6">
                <legend className="text-sm font-semibold">בחרו מידה*</legend>
                <div
                  className="mt-2 flex flex-wrap gap-2"
                  role="radiogroup"
                  aria-label="בחירת מידה"
                >
                  {sizeOptions.map((option) => (
                    <button
                      key={option.sizeGroup}
                      type="button"
                      role="radio"
                      aria-checked={sizeGroupOf(selectedVariant) === option.sizeGroup}
                      onClick={() => selectSize(option.sizeGroup)}
                      className={`variant-option rounded-md border-2 px-4 py-2 font-semibold ${
                        sizeGroupOf(selectedVariant) === option.sizeGroup ? '' : 'border-line'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </fieldset>
              <fieldset className="mt-4">
                <legend className="text-sm font-semibold">{axis2Label}</legend>
                <div
                  className="mt-2 flex flex-wrap gap-2"
                  role="radiogroup"
                  aria-label={axis2Type === 'color' ? 'בחירת צבע' : 'בחירת מוכן או לא מוכן'}
                >
                  {axis2Options.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={axis2Of(selectedVariant) === option.value}
                      onClick={() => selectAxis2(option.value)}
                      className={`variant-option rounded-md border-2 px-4 py-2 font-semibold ${
                        axis2Of(selectedVariant) === option.value ? '' : 'border-line'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </fieldset>
            </>
          ) : (
            variants.length > 0 && (
              <fieldset className="mt-6">
                <legend className="text-sm font-semibold">בחרו מידה*</legend>
                <div
                  className="mt-2 flex flex-wrap gap-2"
                  role="radiogroup"
                  aria-label="בחירת מידה"
                >
                  {variants.map((variant) => (
                    <button
                      key={variant.id}
                      type="button"
                      role="radio"
                      aria-checked={selectedVariantId === variant.id}
                      onClick={() => setSelectedVariantId(variant.id)}
                      className={`variant-option rounded-md border-2 px-4 py-2 font-semibold ${
                        selectedVariantId === variant.id ? '' : 'border-line'
                      }`}
                    >
                      {variant.label}
                    </button>
                  ))}
                </div>
              </fieldset>
            )
          )}

          <div className="mt-6">
            <PricingFields
              pricingType={product.pricingType}
              unitLabel={product.unitLabel}
              minOrderQty={product.minOrderQty}
              qtyStep={product.qtyStep}
              onChange={setPricingResult}
              singleQuantityField={singleQuantityField}
              singleQuantityFieldLabel={singleQuantityFieldLabel}
            />
          </div>

          {product.priceIsEstimate && (
            <p className="banner-warning mt-4 rounded-md p-3 text-sm">
              מוצר זה מיוצר לפי מידה. הצעת המחיר שתקבל היא הערכה ראשונית, והפרטים הסופיים יסוכמו
              מולנו בוואטסאפ.
            </p>
          )}

          {/* ⭐ "מחיר לפי בקשה" — מקביל אורתוגונלי לבאנר "מיוצר לפי מידה" למעלה.
              ⛔ אין מחיר בשום מקום באתר הציבורי ממילא (ראו הערת הראש של הקובץ),
              ולכן אין "מחיר בדוי" להחליף - ההודעה הזו היא כל תצוגת המחיר
              שהמוצר הזה מקבל. המוצר עדיין ניתן להוספה לעגלה - כמות/מידות
              עדיין רלוונטיות, המחיר בלבד לא ידוע. */}
          {product.priceOnRequest && (
            <p className="banner-warning mt-4 rounded-md p-3 text-sm">
              מוצר בהזמנה אישית, יש לפנות בוואטסאפ לקבלת מידע ספציפי על אותו מוצר.
            </p>
          )}

          <button
            type="button"
            className="btn btn-cta mt-6"
            disabled={!pricingResult?.isValid}
            onClick={handleAddToCart}
          >
            הוסף לעגלה
          </button>
          <p role="status" aria-live="polite" className="text-muted mt-2 text-sm">
            {addedMessage}
          </p>

          {product.specHtml && (
            <div className="mt-8">
              <h2 className="text-lg font-bold">מפרט</h2>
              <div
                className="spec-table mt-2"
                // ⚠️ ⛔ בטוח — spec_html עובר sanitize-html בשרת (allow-list צר,
                //    בלי תכונות בכלל) בכל יצירה/עדכון בפאנל, לפני שהוא נשמר ב-DB.
                dangerouslySetInnerHTML={{ __html: product.specHtml }}
              />
            </div>
          )}
        </div>
      </div>

      {/* ⭐ CTA לחזרה לקטלוג - עידוד המשך עיון וקנייה של מוצרים נוספים אחרי
          צפייה במוצר, ⛔ לא רק פירורי הלחם למעלה שקל לפספס בגלילה. */}
      <div className="mt-12 rounded-lg border border-line bg-surface p-6 text-center">
        <h2 className="text-lg font-bold">רוצים לראות עוד מוצרים?</h2>
        <p className="text-muted mt-2">חזרו לקטלוג ובחרו עוד פריטים להוספה לעגלת הבקשות.</p>
        <Link to="/catalog" className="btn btn-secondary mt-4 inline-flex">
          חזרה לקטלוג
        </Link>
      </div>

      {lightboxIndex !== null && lightboxImages.length > 0 && (
        <Lightbox
          images={lightboxImages}
          index={lightboxIndex}
          onNavigate={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
